/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { Result } from "../../network/http-client"
import type { ApplicationType } from "../application-type/application-type-types"

import type { UserAppTypeService } from "./user-apptype-service"
import type { UserAppTypeStore } from "./user-apptype-store"
import type { UserAppTypesState } from "./user-types"

export class UserAppTypeViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly userAppTypeStore: UserAppTypeStore,
        private readonly userAppTypeService: UserAppTypeService
    ) {}

    userAppTypesForOrganization(userId: number, orgId: number | null): ApplicationType[] {
        const userAppTypes = this.userAppTypeStore.getUserAppTypes(userId) ?? []
        return userAppTypes.filter((app) => app.organizationId === orgId)
    }

    getUserAppTypesFetchState(): UserAppTypesState {
        return this.userAppTypeStore.getUserAppTypesFetchState()
    }

    shouldConfirmUnassignment(isExternal: boolean, selectedAppTypeIds: number[]): boolean {
        return isExternal && selectedAppTypeIds.length === 0
    }

    async getUserAppTypes(userId: number): Promise<Result<ApplicationType[]>> {
        this.userAppTypeStore.setUserAppTypesFetchStateForUser(userId, "not-fetched")
        return await this.userAppTypeService.getUserAppTypes(userId)
    }

    async removeExternalUser(userId: number): Promise<Result<void>> {
        const userAppTypes = this.userAppTypeStore.getUserAppTypes(userId)
        const appTypeIds = userAppTypes.map((appType) => appType.id).filter((id): id is number => id !== undefined)

        if (!appTypeIds.length) {
            console.warn("No app types to remove for external user:", userId)
            return { success: false, error: Error("No app types were found") }
        } else {
            return await this.userAppTypeService.removeUserAppTypes(userId, appTypeIds)
        }
    }

    async updateUserAppTypes(userId: number, orgId: number, assignedAppTypeIds: number[]): Promise<Result<void>> {
        this.userAppTypeStore.setUserAppTypesFetchStateForUser(userId, "not-fetched") // show loading
        const currentAppIds = this.userAppTypesForOrganization(userId, orgId)
            .map((app) => app.id)
            .filter((id): id is number => id !== undefined) // Ensure non-null

        const toAdd = assignedAppTypeIds.filter((id) => !currentAppIds.includes(id))
        const toRemove = currentAppIds.filter((id) => !assignedAppTypeIds.includes(id))

        const removeResult = await this.userAppTypeService.removeUserAppTypes(userId, toRemove)
        const addResults = await this.userAppTypeService.assignUserAppTypes(userId, toAdd)

        if (!removeResult.success && !addResults.success) {
            return { success: false, error: Error("Failed to update user apps.") }
        }
        if (!removeResult.success) {
            return removeResult
        }
        if (!addResults.success) {
            return addResults
        }

        return { success: true, data: undefined }
    }
}
